import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Clock, Loader2, X } from 'lucide-react'
import toast from 'react-hot-toast'
import TeamAvatar from './TeamAvatar'
import { useTeamStore } from '../store/useTeamStore'
import { applicationStatusClass, applicationStatusLabel, teamErrorText } from '../utils/teamFormat'
import { formatAgo } from '../../profile/utils/profileFormat'
import { formatSecondsLeft } from '../../../utils/formatters'
import { CARD_CLASS } from '../utils/teamUi'
import { hapticImpact } from '../../../utils/haptic'

/**
 * Мои заявки на вступление: максимум 5 активных, каждая живёт 7 дней.
 * Текст заявки не редактируется — только отозвать и подать заново.
 */
const MyApplicationsList = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { myApplications, isApplicationsLoading, cancelApplication } = useTeamStore()

  if (isApplicationsLoading && myApplications.length === 0) {
    return (
      <div className={`${CARD_CLASS} flex items-center justify-center p-6`}>
        <Loader2 size={20} className="animate-spin text-accent-primary" />
      </div>
    )
  }

  if (myApplications.length === 0) return null

  const pendingCount = myApplications.filter((app) => app.status === 'pending').length

  const handleCancel = async (id) => {
    hapticImpact('medium')
    const res = await cancelApplication(id)
    if (res?.ok) {
      toast.success(t('team.applicationWithdrawn', 'Заявка отозвана'))
    } else if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  return (
    <div className={`${CARD_CLASS} p-4`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-bold not-italic uppercase tracking-wide text-text-secondary">
          {t('team.myApplications', 'Мои заявки')}
        </h2>
        {pendingCount > 1 && (
          <button
            onClick={() => handleCancel(null)}
            className="text-xs text-text-secondary underline-offset-2 transition-colors hover:text-white hover:underline"
          >
            {t('team.cancelAllApplications', 'Отозвать все')}
          </button>
        )}
      </div>

      <div className="space-y-2">
        {myApplications.map((app) => (
          <div key={app.id} className="rounded-xl border border-white/5 bg-dark-primary p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
            <div className="flex items-start gap-3">
              <div
                onClick={() => {
                  hapticImpact('light')
                  navigate(`/teams/${app.team.id}`)
                }}
                className="cursor-pointer transition-transform active:scale-[0.98]"
              >
                <TeamAvatar team={app.team} size={40} />
              </div>

              <div className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-white">{app.team.name}</span>

                {/* Статус — на отдельной строке, чтобы название не сужалось */}
                <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${applicationStatusClass(app.status)}`}>
                  {applicationStatusLabel(app.status, t)}
                </span>

                {app.text && (
                  <p className="mt-1.5 line-clamp-2 text-xs text-text-secondary">{app.text}</p>
                )}

                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-text-secondary">
                  <span>{formatAgo(app.created_ago, t)}</span>
                  {/* expires_in — сколько секунд заявке осталось жить (7 дней с подачи) */}
                  {app.status === 'pending' && app.expires_in != null && (
                    <span className="flex items-center gap-1">
                      <Clock size={11} />
                      {formatSecondsLeft(app.expires_in, t)}
                    </span>
                  )}
                </div>
              </div>

              {app.status === 'pending' && (
                <button
                  onClick={() => handleCancel(app.id)}
                  className="shrink-0 rounded-lg border border-white/10 p-1.5 text-text-secondary transition-all active:scale-[0.98] hover:text-white"
                  aria-label={t('team.cancelApplication', 'Отозвать заявку')}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default MyApplicationsList
